import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * Site header custom element. Three responsibilities:
 *
 *  1. Publishes the live header height as the `--header-height` CSS variable.
 *  2. Swaps the inline nav menu for the drawer when the menu overflows.
 *  3. Flags `data-sticky-state` for styles that key off it (transparent header).
 *
 * The header is always sticky (fixed) purely via CSS — positioning is not driven
 * from here, so there is no scroll/hide logic.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** Window width at which the menu was collapsed into the drawer. @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** Zero-size marker pinned to the top of the document flow. @type {HTMLElement | null} */
  #stickySentinel = null;

  /**
   * Publishes the live header height to `--header-height`, and restores the
   * inline menu from the drawer once the viewport is wide enough again.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Flags `data-sticky-state` ('inactive' at the top of the page, 'active' once
   * the page has scrolled) for dependent styles such as the transparent header.
   *
   * The header is permanently `position: fixed`, so it always fully intersects
   * the viewport and can't tell us when the page has scrolled. Observe a
   * zero-size sentinel pinned to the top of the document flow instead: with
   * `threshold: 1` it stops fully intersecting the moment the page scrolls,
   * flipping us to 'active' — the same boundary the old `top: -1px` sticky
   * trick relied on.
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;inset-block-start:0;inset-inline-start:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);
    this.#stickySentinel = sentinel;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      changeMetaThemeColor(this.refs.headerRowTop);
    }, { threshold: 1 });

    this.#intersectionObserver.observe(sentinel);
  };

  /** @param {OverflowMinimumEvent} event */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Show the drawer (and hide the inline menu) when the menu overflows, or
   * restore the inline menu when there's room for it.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    if (this.getAttribute('sticky')) {
      this.#observeStickyPosition();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#stickySentinel?.remove();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;

  // Keep the header-group height in sync as its children resize or are added/removed.
  const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

  const observeChildren = () => {
    for (const child of headerGroup.children) {
      if (child !== header && child instanceof HTMLElement) resizeObserver.observe(child);
    }
  };

  observeChildren();
  new MutationObserver(observeChildren).observe(headerGroup, { childList: true });
});
